/* Driver for sections/spin-story.
 *
 * Scroll-SCRUBBED, unlike sections/scroll-story, which is deliberately a trigger model:
 * here every visible property is a plain function of scroll progress, written per frame.
 * Nothing transitions, so dragging the scrollbar backwards mirrors it exactly.
 *
 * One shared listener and one rAF for every instance on the page. Reads only
 * getBoundingClientRect on the track; writes only custom properties, never layout. */
(function () {
  /* One binding per page. A second copy of this file — a duplicated section group, an app
     embed that inlines assets, a theme editor hot-reload — would add a second scroll
     listener and a second rAF over the same elements, doubling every write per frame. */
  var NS = '__sd_dac99834792e';
  if (window[NS]) return;
  window[NS] = 1;

  var stories = document.querySelectorAll('[data-spin-story]');
  if (!stories.length) return;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // fallback sheet layout, for a section saved before the grid became a block setting
  var FALLBACK_FRAMES = 30;
  var FALLBACK_COLS = 10;
  var queued = false;

  function clamp(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  /* The row pitch has to be the height of the actual line box: the copy is display type
     whose size the merchant sets freely, and an `em` on the beat would resolve against
     the inherited body size instead - 22px where the word is 109px, which stacked every
     line on top of the last. Measured once per layout change, never during a scroll. */
  function rowStep(story, beats) {
    if (story.__rowStep == null) {
      var mult = parseFloat(getComputedStyle(story).getPropertyValue('--row-mult'));
      if (!(mult > 0)) mult = 1.2;
      story.__rowStep = (beats[0] ? beats[0].offsetHeight : 0) * mult;
    }
    return story.__rowStep;
  }

  /* Marquee mode tiles each row's word across the band so the row has no end inside the
     frame. The copies have to be real elements — CSS cannot repeat an element's own text
     — so the word is cloned until the row covers the band plus the drift it will travel,
     and every clone is hidden from assistive tech and from the editor, which already own
     the one real copy. Layout time only, never during a scroll, and skipped entirely
     while the width is unchanged: a phone's URL bar fires resize on every flick. */
  function tile(story) {
    if (story.dataset.spinStory !== 'marquee') return;
    var band = story.querySelector('.spin-beats');
    if (!band || story.__tileW === band.offsetWidth) return;
    story.__tileW = band.offsetWidth;

    var beats = story.querySelectorAll('[data-spin-beat]');

    /* How far a row actually travels, which is the drift TIMES half the beat count - the
       same scale paintOne applies. Tiling only to one drift left a row running out of
       copies at the edge of the frame the moment the slide got quicker. */
    var drift = parseFloat(getComputedStyle(story).getPropertyValue('--drift')) || 0;
    var reach = (drift / 100) * window.innerWidth * (beats.length / 2);
    var need = band.offsetWidth + 2 * reach;

    for (var i = 0; i < beats.length; i++) {
      var inner = beats[i].querySelector('.spin-beat__inner');
      if (!inner) continue;
      var old = inner.querySelectorAll('[data-spin-tile]');
      for (var o = 0; o < old.length; o++) old[o].remove();

      var word = inner.querySelector('.heading');
      if (!word) continue;
      var unit = word.offsetWidth + (parseFloat(getComputedStyle(word).marginInlineEnd) || 0);
      if (!(unit > 0)) continue;

      // capped, so a two-letter word at a small size cannot ask for a thousand nodes
      /* Built as spans rather than cloned, deliberately. A cloneNode of the beat's own
         h3 tiles the HEADING with it - six rows of five copies put 26 extra h3s in the
         home page's outline, aria-hidden or not. The class and the inline style are what
         `.heading` renders from, so a span carrying both is identical on screen and is
         nothing at all in the document outline. Any wave decoration is dropped with the
         markup, which is right: the tiling is texture, not a heading. */
      var copies = Math.min(60, Math.ceil(need / unit) - 1);
      for (var c = 0; c < copies; c++) {
        var clone = document.createElement('span');
        clone.className = word.className;
        clone.setAttribute('style', word.getAttribute('style') || '');
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-spin-tile', '');
        clone.textContent = word.textContent;
        inner.appendChild(clone);
      }
    }
  }

  /* The stage's disc, when the merchant pointed it at the beats. The beat's own heading
     and line are the source; this writes a second, readable presentation of them into the
     disc and replays its entry. Only ever on a CHANGE of beat - a per-frame write would
     restart the animation on every scroll event and the disc would never finish arriving.

     Reading textContent off elements the section already rendered is deliberate: a static
     block cannot see the beats, and the beats cannot see the stage, so one of the two has
     to be filled from the outside and the driver is the only thing that sees both. */
  function writeDisc(story, beat, spot) {
    var disc = story.querySelector('[data-spin-disc]');
    var callout = story.querySelector('[data-spin-callout]');
    if (callout) callout.dataset.spot = spot;
    if (!disc || !beat) return;

    var word = beat.querySelector('.heading');
    var line = beat.querySelector('.text');
    var valueEl = disc.querySelector('[data-spin-disc-value]');
    var labelEl = disc.querySelector('[data-spin-disc-label]');
    if (valueEl) valueEl.textContent = word ? word.textContent.trim() : '';
    if (labelEl) labelEl.textContent = line ? line.textContent.trim() : '';
  }

  /* The disc's copy and the callout's spot both change on a beat change, and the callout
     has to pack itself away before it reappears somewhere else - so this is exit, THEN
     swap, THEN entry, not one cross-fade. The pending timer is cancelled on every change:
     scrolled quickly, a beat that never finished leaving is simply overtaken and the
     annotation lands at the newest spot instead of queueing up stale ones. */
  // must match the exit animations in blocks/_spin-stage.liquid, which end at 290ms
  var EXIT_MS = 290;
  // how still the scroll has to be before the annotation comes back
  var SETTLE_MS = 150;
  /* Hard cap on the wait, measured from when the retraction STARTED (__leftAt), not from
     the last change. Without it a phone fling never has a quiet SETTLE_MS: the beat keeps
     changing for a second or more, `__changedAt` keeps sliding forward, and arrival keeps
     being pushed out until the visitor is past the whole section without the callout ever
     landing once. 640ms sits above EXIT_MS + SETTLE_MS (440ms, the normal settle case, so
     it never fires during ordinary scrolling) and below the ~700ms+ of a full retract-then-
     enter cycle - close enough to that natural cadence that forcing a landing here reads as
     the callout keeping pace with a long scroll rather than stuttering into it. */
  var CEILING_MS = 640;

  /* The callout retracts on the FIRST beat change and then waits for the scroll to stop
     before it comes back, rather than trying to keep up with it.

     Keeping up is not achievable and was never worth attempting. One phone flick walks
     several beats in a few hundred milliseconds; a retract-swap-enter cycle is over 700ms.
     Replaying it per change meant the annotation spent every long scroll part-way through
     an entry that was immediately cut into another exit, so on a phone it never appeared at
     all and on a desktop it flickered. Waiting is also what it should do: an annotation
     pointing at a spot on a stick has nothing to say while the stick is flying past.

     So arrival is due at the later of two moments - the retraction having finished, and the
     beat having held still for SETTLE_MS - and every further change just pushes the second
     one out. One timer, recomputed, never cancelled into nothing. */
  function fillDisc(story, beat, spot) {
    var callout = story.querySelector('[data-spin-callout]');
    if (!callout) return;

    // whatever the newest beat is when the wait finally expires is the one that lands
    story.__pending = { beat: beat, spot: spot };
    story.__changedAt = Date.now();

    function arrive() {
      story.__timer = null;
      story.__leftAt = 0;
      var next = story.__pending;
      writeDisc(story, next.beat, next.spot);
      callout.classList.remove('is-idle');
      callout.classList.remove('is-leaving');
      callout.classList.remove('is-entering');
      void callout.offsetWidth; // reflow, or the class never leaves and nothing replays
      callout.classList.add('is-entering');
    }

    // Where the dot and the line are not drawn there is nothing to retract, so it just swaps.
    if (!story.querySelector('.spin-stage--kunye')) {
      arrive();
      return;
    }

    if (!story.__leftAt) {
      callout.classList.remove('is-entering');
      callout.classList.add('is-leaving');
      story.__leftAt = Date.now();
    }

    clearTimeout(story.__timer);
    var settled = Math.max(story.__leftAt + EXIT_MS, story.__changedAt + SETTLE_MS);
    var due = Math.min(settled, story.__leftAt + CEILING_MS) - Date.now();
    story.__timer = setTimeout(arrive, due > 0 ? due : 0);
  }

  /* Packs the annotation away and forgets which beat was live, so re-entering the section
     plays an arrival instead of revealing whatever was on screen when it was last left.

     This used to jump straight to is-idle, which is visibility: hidden with no transition -
     the one path that skipped the exit animation entirely, because it never touched
     is-leaving at all. Now it retracts the same way a beat change does and only hides once
     that has had time to finish. Nothing to retract (no künye, or already idle) still
     skips straight to hidden - there is no CSS exit to wait for either way. */
  function idleDisc(story) {
    story.__live = null;
    clearTimeout(story.__timer);
    var callout = story.querySelector('[data-spin-callout]');
    if (!callout) return;

    if (callout.classList.contains('is-idle') || !story.querySelector('.spin-stage--kunye')) {
      story.__timer = null;
      story.__leftAt = 0;
      callout.classList.remove('is-entering');
      callout.classList.remove('is-leaving');
      callout.classList.add('is-idle');
      return;
    }

    callout.classList.remove('is-entering');
    callout.classList.add('is-leaving');
    story.__leftAt = Date.now();
    story.__timer = setTimeout(function () {
      story.__timer = null;
      story.__leftAt = 0;
      callout.classList.remove('is-leaving');
      callout.classList.add('is-idle');
    }, EXIT_MS);
  }

  /* The sprite grid is a block setting now, published as unitless custom properties on
     .spin-stage - --sheet-frames and --sheet-cols (a --sheet-rows travels alongside them,
     but paintOne never needs the row count itself: it falls out of dividing by cols).
     Read once per layout change, same as rowStep and tileW, never inside the scroll path -
     paintOne runs on every frame. A section saved before this setting existed carries
     neither property, so an unparseable or non-positive read falls back to the sheet the
     theme ships. */
  function measureSheet(story) {
    var frames = FALLBACK_FRAMES;
    var cols = FALLBACK_COLS;
    var stage = story.querySelector('.spin-stage');
    if (stage) {
      var cs = getComputedStyle(stage);
      var f = parseInt(cs.getPropertyValue('--sheet-frames'), 10);
      var c = parseInt(cs.getPropertyValue('--sheet-cols'), 10);
      if (f > 0) frames = f;
      if (c > 0) cols = c;
    }
    story.__frames = frames;
    story.__cols = cols;
  }

  /* The band's height in PIXELS, published for the CSS to size the stage and the wall from.

     This was `100cqh` with `container-type: size` on the band, which reads better and cost
     far too much: a font-size that depends on a container's height makes every style
     recalculation inside that container resolve container units, and the browser has to be
     conservative about the layout dependency that creates. Measuring it once per layout and
     handing the CSS a plain length is the same answer with none of that - and it is the
     pattern the rest of this file already uses for rowStep and tileW. */
  function measureBand(story) {
    var band = story.querySelector('.spin-story__body');
    if (!band) return;
    var h = band.getBoundingClientRect().height;
    if (h > 0) story.style.setProperty('--band-px', Math.round(h) + 'px');
  }

  function remeasure(force) {
    for (var i = 0; i < stories.length; i++) {
      // cached so the scroll path never touches the DOM looking for things that cannot move
      stories[i].__track = stories[i].querySelector('[data-spin-track]');
      stories[i].__stage = stories[i].querySelector('.spin-stage');
      stories[i].__beats = stories[i].querySelectorAll('[data-spin-beat]');
      stories[i].__pips = stories[i].querySelectorAll('[data-spin-rail] i');
      measureBand(stories[i]);
      stories[i].__rowStep = null;
      if (force) stories[i].__tileW = null;
      tile(stories[i]);
      measureSheet(stories[i]);
    }
  }

  function paintOne(story) {
    var track = story.__track;
    if (!track) return;

    var rect = track.getBoundingClientRect();

    /* Nothing to paint while the track is off screen, and this is the single most expensive
       line in the file. Every write below lands on a custom property, and a custom property
       is INHERITED - writing one invalidates the computed style of the whole subtree under
       the element it is written to. Without this guard that happened on every scroll frame
       of the entire page, for a section that might be five screens away, and the section's
       subtree is the six-row wall plus its tiling. Style and layout, not script, is where
       that shows up in a profile. */
    if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
      if (story.__live !== null) idleDisc(story);
      return;
    }

    var run = rect.height - window.innerHeight;

    /* TWO progresses. `p` is clamped and owns everything that has to land on a beat - the
       frame, the lit row, the disc - because those are choreography and must not run past
       their ends. `pRaw` is the same number unclamped, which is negative while the track is
       still coming up the screen and greater than 1 once it is leaving.

       The wall rides pRaw, so its type keeps sliding as you scroll INTO the section and as
       you scroll past it, instead of sitting frozen until the pin catches and freezing again
       the moment it lets go. It needs no bounds of its own: the track is only painted while
       part of it is on screen, which holds pRaw to roughly -0.2..1.2. */
    var pRaw = run > 0 ? -rect.top / run : 0;
    var p = clamp(pRaw);

    /* Written on the STAGE, not the section. --p, --col and --row are read only by the
       sprite and the uploaded still, both inside .spin-stage; writing them on the section
       root threw away the computed style of every beat and every tile as well, to move a
       stick. */
    var stage = story.__stage || story;
    stage.style.setProperty('--p', p.toFixed(4));

    /* One full turn over the scroll, wrapping so the last frame meets the first. The
       frame SNAPS - there is no blend with the next one. Cross-fading two rotations 12
       degrees apart at half opacity each is a genuine double image: sharp on a frame and
       doubled between two, which is what read as a blurry stick. Thirty frames over a
       multi-screen track hold long enough that snapping is not visible. */
    var frames = story.__frames || FALLBACK_FRAMES;
    var cols = story.__cols || FALLBACK_COLS;
    var frame = Math.floor(p * frames) % frames;
    stage.style.setProperty('--col', frame % cols);
    stage.style.setProperty('--row', Math.floor(frame / cols));

    // cached at layout time: querySelectorAll on every frame is work for no new answer
    var beats = story.__beats;
    var pips = story.__pips;
    var n = beats.length;
    if (!n) return;

    /* Where the focus band stands, in beats. It has to be ONE number: the disc's words and
       the lit row are the same beat, so deriving them separately lets them disagree.

       `p * (n - 1)` walks 0..n-1, which reads right and rations wrong - rounding it gives
       the first and last beat HALF a slice of scroll each. On a six-beat story the opening
       beat swapped 320px into a 3200px track, so it retracted before it had finished
       arriving, which is the skip you feel the moment you start scrolling. Offsetting by
       half a slice gives all six an equal 1/n; the clamp keeps the ends fully resolved
       rather than starting the stack mid-move. */
    var walk = clamp((p * n - 0.5) / (n - 1)) * (n - 1);

    for (var i = 0; i < n; i++) {
      var beat = beats[i];
      var lit;

      var mode = story.dataset.spinStory;

      if (mode === 'wall' || mode === 'marquee') {
        // both walk one focus band down the stack; only what moves differs
        var rows = i - walk;
        lit = clamp(1 - Math.abs(rows));

        /* Marquee lifts the live row without taking it to full: the plinth cuts the middle
           out of whichever row is lit, so a row at 1 only LOOKS like copy - the disc
           carries the words. It still has to be unmistakable though. At 0.24 the highlight
           had effectively gone; half opacity reads as the live row against 0.13 texture
           without ever pretending to be something you should try to read. */
        var base = mode === 'marquee' ? 0.11 : 0.13;
        var floor = mode === 'marquee' ? 0.15 : 0.87;
        beat.style.setProperty('--o', (base + floor * lit * lit).toFixed(3));

        if (mode === 'wall') {
          // the whole stack travels past the band in the middle
          beat.style.setProperty('--y', (rows * rowStep(story, beats)).toFixed(1));
          beat.style.setProperty('--x', reduce ? 0 : (rows * (i % 2 ? -1 : 1)).toFixed(3));
          beat.style.setProperty('--s', (0.84 + 0.16 * lit).toFixed(3));
        } else {
          /* The rows hold still and their tiling slides, alternating direction by row.
             Scaled by the beat count so ONE BEAT of scroll moves a row by one drift: over
             the whole track --x ran -1..1 whatever the length, which on a six-beat story
             is a crawl - the tiling exists so the row can move properly. */
          var slide = (2 * pRaw - 1) * (n / 2);
          beat.style.setProperty('--x', reduce ? 0 : (slide * (i % 2 ? -1 : 1)).toFixed(3));
        }
      } else {
        // each word owns a 1/n slice and crosses the band right to left
        var d = (p - (i + 0.5) / n) * n;
        lit = clamp(1.3 - Math.abs(d) * 2.3);
        beat.style.setProperty('--o', lit.toFixed(3));
        beat.style.setProperty('--o2', clamp(1.5 - Math.abs(d + 0.06) * 3.2).toFixed(3));
        beat.style.setProperty('--x', reduce ? 0 : (-d).toFixed(3));
      }

      if (pips[i]) pips[i].style.setProperty('--w', lit.toFixed(3));
    }

    /* The disc only runs while the pin actually holds the viewport. It used to be driven
       from the first paint, which happens at page load with the section several screens
       below: the opening beat resolved there and then, so it was already sitting open when
       the visitor finally reached it instead of arriving as they entered. */
    /* The pin holds only while the track still has a screen of itself below the fold, so
       `rect.bottom <= innerHeight` is the moment it lets go. Retracting on `<= 0` instead
       waited for the whole track to clear the viewport, by which time the last beat's disc
       had scrolled off still fully drawn - it never appeared to leave, it just went away
       with the section. */
    if (rect.top > 0 || rect.bottom <= window.innerHeight) {
      if (story.__live !== null) idleDisc(story);
      return;
    }

    // whichever beat the focus band is nearest owns the disc, and the spot cycles with it
    var live = Math.round(walk);
    if (story.__live !== live) {
      story.__live = live;
      fillDisc(story, beats[live], live % 3);
    }
  }

  function paint() {
    queued = false;
    for (var i = 0; i < stories.length; i++) paintOne(stories[i]);
  }

  function request() {
    if (!queued) {
      queued = true;
      requestAnimationFrame(paint);
    }
  }

  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', function () {
    remeasure();
    request();
  }, { passive: true });
  // the editor re-renders a section in place, which replaces the elements this holds
  document.addEventListener('shopify:section:load', function () {
    stories = document.querySelectorAll('[data-spin-story]');
    remeasure();
    request();
  });
  remeasure();
  paint();
  // a word measured in the fallback face tiles to the wrong count
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      remeasure(true);
      request();
    });
  }
})();
